import { defineStore } from 'pinia';
import i18n from '@/plugins/vue-i18n';

import { useOrdersStore } from '@/stores/orders.store';
import { Pagination } from '@/types/api/response.types';
import { useToastStore } from '@/stores/utils/toast.store';
import {
  getAllReturns,
  getReturnDetails,
  getReturnDoc,
  getReturnsCount,
  getReturnsCreatedBy,
  getReturnsSites,
  getReturnsStatuses,
  logReturn,
  updateReturnStatus,
  uploadReturnDocuments,
} from '@/api/returns.service';
import {
  LogReturnData,
  Return,
  ReturnCreatedBy,
  ReturnListItem,
  ReturnPaginationParams,
  ReturnParams,
  ReturnSite,
  ReturnSiteParams,
  ReturnStatusUpdateBody,
} from '@/types/api/returns.types';
import { documentUploadToast } from '@/common/utils/general.utils';
import { DocumentUploadResponse } from '@/types/api/orders.types';
import { toRefs } from 'vue';
import { useTotalsStore } from '@/stores/utils/totals.store';

const { t } = i18n.global;

// Overlapping list requests (paging, filtering, a refetch after a status change) can resolve out of
// order; only the most recent one may write the list.
let latestReturnsRequest = 0;

export const useReturnsStore = defineStore('returns', {
  state: () => ({
    loggedReturn: {} as Return,
    returnDetails: {} as Return,
    returnsStatuses: [] as string[],
    returnsSites: [] as ReturnSite[],
    returnsList: [] as ReturnListItem[],
    returnDoc: {} as DocumentUploadResponse,
    returnsCreatedBy: [] as ReturnCreatedBy[],
    viewingOrder: false,
    viewingReturn: false,

    returnsPagination: {} as Pagination,

    returnParams: {
      pageNo: 1,
      pageSize: 20,
      orderByDesc: 'CreatedAt',
    } as ReturnPaginationParams,
  }),

  actions: {
    async logReturn(data: LogReturnData) {
      const orderStore = useOrdersStore();
      const toastStore = useToastStore();
      const response = await logReturn(data);

      if (response.status === 200) {
        this.loggedReturn = response.data.data;
        toastStore.showToast(
          'success',
          t('returns.logReturn.successToast.title'),
          t('returns.logReturn.successToast.content', {
            returnNumber: this.loggedReturn.returnNumber,
          }),
        );
        await orderStore.getOrderDetailsById(this.loggedReturn.order.id);
      }
    },

    async updateReturnStatus(id: string, data: ReturnStatusUpdateBody) {
      const response = await updateReturnStatus(id, data);

      if (response.status === 200) {
        this.returnDetails = response.data.data;
        let matchFound = false;

        this.returnsList = this.returnsList.map((returnItem) => {
          if (returnItem.id === id) {
            matchFound = true;
            return { ...returnItem, status: response.data.data.status };
          }

          return returnItem;
        });

        if (!matchFound) {
          console.warn(`Return ID ${id} not found in returns list.`);
        }
      }
    },

    async getReturnDetails(id: string) {
      const response = await getReturnDetails(id);

      if (response.status === 200) {
        this.returnDetails = response.data.data;
      }
    },

    async getAllReturns(params: ReturnPaginationParams) {
      const requestId = ++latestReturnsRequest;
      // Saved before the request so a caller that starts after this one (Issue Return, Log
      // Payment) builds on these filters; whichever request is latest then shows the right rows.
      this.returnParams = { ...this.returnParams, ...params };
      const response = await getAllReturns(this.returnParams);

      if (requestId !== latestReturnsRequest) return;

      const { pageCount, totalCount, pageNo, pageSize } = response.data.meta.pagination;
      this.returnsPagination = { pageCount, totalCount, pageNo, pageSize };

      this.returnsList = response.data.data;
    },

    async getReturnsCount(params: ReturnParams) {
      const { returnsByOrgCount, returnsByVendorCount } = toRefs(useTotalsStore());
      const response = await getReturnsCount(params);

      if (response.status === 200) {
        if (params.accountPayableId) {
          returnsByVendorCount.value = response.data.data.pendingCount;
        } else {
          returnsByOrgCount.value = response.data.data.pendingCount;
        }
      }
    },

    async getReturnsStatuses() {
      const response = await getReturnsStatuses();

      if (response.status === 200) {
        this.returnsStatuses = response.data.data;
      }
    },

    async getReturnsSites(params: ReturnSiteParams) {
      const response = await getReturnsSites(params);

      if (response.status === 200) {
        this.returnsSites = response.data.data;
      }
    },

    async getReturnsCreatedBy(params: ReturnSiteParams) {
      const response = await getReturnsCreatedBy(params);

      if (response.status === 200) {
        this.returnsCreatedBy = response.data.data;
      }
    },

    async uploadReturnDocuments(returnId: string, data: FormData) {
      const response = await uploadReturnDocuments(returnId, data);

      documentUploadToast({
        responseCode: response.status,
        title: t('returns.details.documentation.toast.uploadSuccess.title'),
        description: t('returns.details.documentation.toast.uploadSuccess.content'),
        onSuccess: async () => {
          await this.getReturnDetails(returnId);
        },
      });
    },

    async getReturnDoc(returnId: string, documentId: string) {
      const response = await getReturnDoc(returnId, documentId);

      this.returnDoc = response.data.data;
    },
  },
});
